import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'

const Calendar = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        Calendar Sync connects your Google Calendar to Guild so that{' '}
        <Link href='/docs/events/overview'>Events</Link> you RSVP to are
        automatically added to your calendar. When you cancel an RSVP or an
        Event is updated, the calendar entry is updated or removed
        automatically — no manual effort required.
      </Paragraph>
      {/* TODO: Capture screenshot of user Settings → Calendar section showing the Connect Google Calendar button */}
    </Card>

    <Card gap='$4'>
      <DocSection id='connecting-google-calendar' title='Connecting Google Calendar'>
        <Paragraph size='$5'>
          To enable Calendar Sync, follow these steps.
        </Paragraph>
        <DocStep
          number={1}
          title='Open your account Settings'
          description='Click your profile photo in the top-right corner of the navigation bar and select Settings.'
        />
        <DocStep
          number={2}
          title='Find the Calendar section'
          description='Scroll to the Calendar section in your Settings. You will see an option to connect your Google Calendar.'
        />
        <DocStep
          number={3}
          title='Connect your Google account'
          description='Click Connect Google Calendar and sign in with the Google account that has the calendar you want to use. Grant the necessary permissions for Guild to create and update calendar events.'
        />
        <DocStep
          number={4}
          title='Start RSVPing'
          description='Once connected, any Event you RSVP to on Guild will automatically appear in your Google Calendar with the Event details, time, and location.'
        />
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='automatic-vs-manual' title='Automatic Sync vs Manual Add'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar-multiple' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Automatic sync</Paragraph> is the
              recommended approach. After connecting Google Calendar, every RSVP
              creates a calendar entry and every cancellation removes it. Event
              updates — such as a time or venue change — are reflected in your
              calendar automatically.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Manual add</Paragraph> is
              available for individual Events even without connecting Google Calendar.
              On any Event page, you can click the{' '}
              <Paragraph size='$5' fontWeight='$7'>Add to Calendar</Paragraph> button
              to download an .ics file or open it directly in your calendar app. This
              is a one-time action — future changes to the Event will not be synced.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='ical-feeds' title='iCal Calendar Feeds'>
        <Paragraph size='$5'>
          If you use Apple Calendar, Outlook, or another calendar app that does
          not support Google Calendar integration, you can subscribe to iCal
          calendar feeds instead. These feeds provide a live, read-only view of a
          Guild's Events that updates automatically.
        </Paragraph>
        <Paragraph size='$5'>
          iCal feeds are set up per Guild rather than per user account. To learn
          how to subscribe, see{' '}
          <Link href='/docs/guilds/calendar-feeds'>Calendar Feeds</Link>.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='disconnecting' title='Disconnecting Calendar Sync'>
        <Paragraph size='$5'>
          To disconnect Google Calendar, return to your account Settings and click{' '}
          <Paragraph size='$5' fontWeight='$7'>Disconnect</Paragraph> in the
          Calendar section. Existing calendar entries created by Guild will remain
          in your Google Calendar, but future RSVPs will no longer sync
          automatically.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default Calendar
